import CheckCircleOutlineOutlinedIcon from '@mui/icons-material/CheckCircleOutlineOutlined';
import { Select, SelectChangeEvent, useModal } from '@shape-ds/components';
import { CaseForm, CaseStatus, caseStatus, CaseStatusEnum } from 'queries/cases/types';
import { useContext, useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import './style.scss';
import { useGetCaseSelectModal } from './useGetCaseSelectModal';
import { useGetSelectOptions } from './useGetSelectOptions';
import { useOptimisticUpdateQuery } from 'queries/events/records';
import { AppContext } from 'appContext';
import { RecordCategory } from 'queries/events/records/types';
import PermissionedButton from 'components/PermissionedButton/PermissionedButton';
import { useLocation } from 'react-router-dom';

type StatusSelectWithButtonProps = {
  onClick: (e?: React.BaseSyntheticEvent) => void;
  isFormDisabled: boolean;
  isFormLoading: boolean;
  className?: string;
};
export const getIsAdvancingStage = (current: CaseStatus, previous: CaseStatus) => {
  return CaseStatusEnum[current] > CaseStatusEnum[previous];
};

const name = 'status';

export const StatusSelectWithButton = ({
  onClick,
  isFormDisabled,
  className,
  isFormLoading,
}: StatusSelectWithButtonProps) => {
  const { t } = useTranslation();
  const { openModal } = useModal();
  const location = useLocation();
  const { formState, getValues, trigger, clearErrors, setValue } = useFormContext<CaseForm>();
  const { isValid, isSubmitSuccessful, isSubmitting, isDirty } = formState;
  const nextStatus = getValues('status') ?? 'diagnosis';
  const isLoading = isFormLoading || isSubmitting;
  const isDisabled = isFormDisabled || isLoading || (!isDirty && !location?.state?.lastEditedField);
  const previousValue = formState?.defaultValues?.status;
  const [saved, setSaved] = useState(false);
  const { state } = useContext(AppContext);

  const eventId = getValues('id');
  const optimisticUpdate = useOptimisticUpdateQuery(['RECORDS', eventId]);

  const handleSave = async () => {
    if (isValid) {
      optimisticUpdate({
        user_email: state.auth.user?.email || '',
        category: RecordCategory.STATUS_UPDATED,
        comment: getValues('status') ?? '',
      });
      onClick();
    }

    if (isSubmitSuccessful) {
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    }
  };

  const { modalCanceled, modalClearEvidences } = useGetCaseSelectModal(handleSave, nextStatus);
  const { formattedOptions } = useGetSelectOptions();

  const handleChange = (currentValue: string) => {
    const isValid = trigger();

    const currentOption = formattedOptions.find((option) => option.value === currentValue);
    const isAdvancingStage =
      CaseStatusEnum[currentValue as keyof typeof CaseStatusEnum] >
      CaseStatusEnum[previousValue as keyof typeof CaseStatusEnum];

    if (
      (isAdvancingStage && previousValue !== caseStatus.canceled) ||
      currentValue === caseStatus.diagnosis
    ) {
      if (!isValid || currentOption?.isBlocked) {
        setValue(name, previousValue);
        return openModal(modalClearEvidences);
      }
    }
    handleSave();
  };

  return (
    <Controller
      name={name}
      rules={{ required: t('components:inputs.required_field') }}
      render={({ field: { onChange, value } }) => {
        return (
          <div className={`status-select-wrapper ${className}`}>
            <Select
              variant="outlined"
              data-testid="case-status-select"
              className="status-select"
              data={formattedOptions}
              onChange={(event: SelectChangeEvent<unknown>) => {
                if (event.target.value === caseStatus.canceled) {
                  clearErrors('cancel_reason');
                  return openModal(modalCanceled);
                }
                onChange(event);
                handleChange(event.target.value as string);
              }}
              value={value}
              name={name}
              startAdornment={t('components:statusSelectWithButton.label.selectPrefix')}
              onOpen={() => {
                trigger();
              }}
              blockedOptionTooltipText={(optionValue) =>
                t(`case:Cases.options.tooltip.${optionValue}`)
              }
              disabled={isDisabled}
            />
            <PermissionedButton
              onClick={handleSave}
              disabled={isDisabled}
              buttonVariant="primary"
              isLoading={isLoading}
              overrideDisableTooltip={t('case:tooltips.no_changes')}
            >
              {saved ? (
                <div className="status-select-button-wrapper">
                  <CheckCircleOutlineOutlinedIcon className="status-select-button-icon" />
                  <span className="status-select-button-text">
                    {t('components:statusSelectWithButton.label.saved')}
                  </span>
                </div>
              ) : (
                t('components:statusSelectWithButton.label.save')
              )}
            </PermissionedButton>
          </div>
        );
      }}
    />
  );
};
